import { CreateAsset, getAsset, isAssetIdExists, isAssetNameExists, UpdateAsset } from "api/assets.api";
import { getUnassignedBeaconsList } from "api/beacons.api";
import { ErrorResponseDto } from "api/config/ErrorResponseDto";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "stores/RootStore";
import { AssetDto } from "types/asset";
import { KeyValuePair } from "utils/common";
import { MinimumLengthValidation, required } from "utils/formstate";
import { getUUID, isDeepEqual } from "utils/utils";

export class AssetStore {
  root: RootStore;
  dataLoading = false;
  error: ErrorResponseDto | null = null
  allAssetTypes: KeyValuePair[] = [];
  allOwners: KeyValuePair[] = [];
  currentAsset: AssetDto = new AssetDto()
  assetForm = this.createForm();
  saveLoading: boolean = false;
  ischangesSaved: boolean = false
  unassignedBeacons: KeyValuePair[] = [];

  constructor(root: RootStore) {
    this.root = root;

    makeObservable(this, {
      dataLoading: observable,
      error: observable,
      saveLoading: observable,
      assetForm: observable,
      ischangesSaved: observable,
      unassignedBeacons: observable,

      reset: action.bound,
      loadAsset: action.bound,
      getUnassignedBeacons: action.bound,
      SaveAsset: action.bound,

      isAnyChange: computed,
      isFormEmpty: computed,
    });

    this.assetForm.validate();
  }

  createForm() {
    return new FormState({
      asset_id: new FieldState(this.currentAsset?.data.asset_id || getUUID()).validators(required),
      asset_name: new FieldState(this.currentAsset?.data.asset_name).validators(required, MinimumLengthValidation),
      asset_type: new FieldState(this.currentAsset?.data.asset_type_id).validators(required),
      owner: new FieldState(this.currentAsset?.data.owner_id).validators(required),
      description: new FieldState(this.currentAsset?.data.description),
      assigned_beacon_id: new FieldState(this.currentAsset?.data.assigned_beacon_id)
    });
  }

  getUnassignedBeacons() {
    return getUnassignedBeaconsList()
      .then((res) => {
        this.unassignedBeacons = [
          ...this.unassignedBeacons,
          ...res.items.map((beacon) => ({ key: beacon.beacon_id, value: beacon.beacon_id }))
        ]
      })
  }

  loadAsset(asset_id: string) {
    this.dataLoading = true
    this.error = null

    return getAsset(asset_id)
      .then((res) => {
        this.currentAsset = res
        this.assetForm = this.createForm();
        this.dataLoading = false

        if (res.data.assigned_beacon_id) {
          this.unassignedBeacons = [
            ...this.unassignedBeacons,
            {
              key: res.data.assigned_beacon_id,
              value: res.data.assigned_beacon_id
            }
          ]
        }
      })
      .catch((error: ErrorResponseDto) => this.error = error)
  }

  fillForm(data: AssetDto | null) {
    if (data) {
      this.currentAsset = data;
    }
    this.assetForm = this.createForm();
    this.assetForm.validate();
  }

  reset() {
    this.currentAsset = new AssetDto();
    this.assetForm = this.createForm();
    this.assetForm.validate();
    this.dataLoading = false
    this.saveLoading = false
    this.ischangesSaved = false
    this.error = null
    this.unassignedBeacons = [];
  }

  BuildAssetDto() {
    const obj = new AssetDto();
    obj.data.asset_id = this.assetForm.$.asset_id.value
    obj.data.asset_name = this.assetForm.$.asset_name.value
    obj.data.asset_type_id = this.assetForm.$.asset_type.value
    obj.data.owner_id = this.assetForm.$.owner.value
    obj.data.description = this.assetForm.$.description.value
    obj.data.assigned_beacon_id = this.assetForm.$.assigned_beacon_id.value
    if (this.currentAsset) {
      obj.revision = this.currentAsset.revision
    }
    return obj
  }

  async SaveAsset() {
    this.saveLoading = true;
    const obj = this.BuildAssetDto()

    const duplicateName = await isAssetNameExists(this.currentAsset?.data.asset_id || "", obj.data.asset_name)
    if (duplicateName) {
      this.saveLoading = false;
      this.assetForm.$.asset_name.setError("Asset name specified already exists.")
      return Promise.reject()
    }

    if (this.currentAsset.data.asset_id === "") {
      const duplicateId = await isAssetIdExists(obj.data.asset_id)
      if (duplicateId) {
        this.saveLoading = false;
        this.assetForm.$.asset_id.setError("Asset ID specified already exists.")
        return Promise.reject()
      }
    }

    const promise = this.currentAsset.data.asset_id !== ""
      ? UpdateAsset(obj)
      : CreateAsset(obj.data)
    return promise
      .then(() => this.ischangesSaved = true)
      .catch((error) => Promise.reject(error))
      .finally(() => this.saveLoading = false)
  }

  get isAnyChange() {
    const srcObj = this.currentAsset;
    const finalObj = this.BuildAssetDto();
    const debug = false;

    //............... always return true for create mode .....................
    if (!this.currentAsset) {
      return false;
    }
    if (!isDeepEqual(srcObj, finalObj,
      ["revision", "last_update_timestamp", "creation_timestamp", "_version"], debug)) {
      return true;
    }
    return false;
  }

  get isFormEmpty() {
    return this.assetForm.$.asset_name.value === "" &&
      this.assetForm.$.asset_type.value === "" &&
      this.assetForm.$.owner.value === "" &&
      this.assetForm.$.description.value === ""
  }
}
